import { WarningIcon } from '@phosphor-icons/react';
import React from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { UnchangedResend } from '@/services/data/ApprovalService';

/**
 * Shown in both send dialogs when an item being re-sent was last answered
 * "changes requested" and its wording is identical to what was decided.
 *
 * The client's request is quoted so the operator can see what was asked for.
 * It never blocks the send — resending unchanged work is sometimes right (the
 * change was made somewhere the snapshot does not carry) — it only says the
 * requested change is not in the text going out.
 */
export const UnchangedResendNotice: React.FC<{ resends: UnchangedResend[] }> = ({ resends }) => {
  if (resends.length === 0) return null;
  return (
    <Alert variant="warning">
      <WarningIcon className="size-4" weight="regular" aria-hidden="true" />
      <AlertTitle>
        {resends.length === 1
          ? 'This item is unchanged since changes were requested'
          : `${resends.length} items are unchanged since changes were requested`}
      </AlertTitle>
      <AlertDescription>
        <p className="text-muted-foreground">
          The wording going out is the same as what was sent back, so the change they asked for is
          not in it yet.
        </p>
        <ul className="mt-2 space-y-1.5">
          {resends.slice(0, 5).map((resend) => (
            <li key={resend.key} className="text-caption">
              <span className="font-medium">{resend.title}</span>
              {resend.comment ? (
                <span className="text-muted-foreground"> — &ldquo;{resend.comment}&rdquo;</span>
              ) : null}
            </li>
          ))}
          {resends.length > 5 ? (
            <li className="text-caption text-muted-foreground">and {resends.length - 5} more</li>
          ) : null}
        </ul>
      </AlertDescription>
    </Alert>
  );
};
